import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  BreadcrumbNav,
  Button,
  Card,
  DataTable,
  Skeleton,
  StatusMessage,
  useToast,
} from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Filters, Pagination } from '../../components/uiChrome.jsx';
import { AlertTriangle, Plus, Store } from 'lucide-react';
import { useIntegrations } from '../../context';
import { useRequestsInbox, useEffectiveRequester } from '../../hooks/requests';
import { useIntegrationsPermissions } from '../../hooks';
import { REQUEST_STATUS_FILTERS } from '../../constants';
import { formatRelativeTime, initialsOf, REQUESTER_AVATAR_CLASS } from '../../utils';
import { approveRequest, bulkUpdateRequests, rejectRequest } from '../../../../services';
import RequestDetailPanel from './RequestDetailPanel.jsx';
import RequestRowActionsMenu from './RequestRowActionsMenu.jsx';
import RequestStatusChip from './RequestStatusChip.jsx';
import RequestsBulkBar from './RequestsBulkBar.jsx';

const PAGE_WRAP = 'flex min-h-0 flex-1 flex-col bg-surface-base';
const PAGE_INNER = 'px-6';
const DEFAULT_PAGE_SIZE = 10;
const SENSITIVITY_TAGS = ['pii', 'financial', 'compliance'];

const formatUsd = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);

const actionLabel = (row) => (Array.isArray(row.action_types) ? row.action_types : []).join(' + ');

/**
 * Requests Inbox (CT-4200, admin-only). Prototype `#/requests`: header + search /
 * status Filters over a workspace DataTable (Status · Request · Requester ·
 * Quote/ETA) with selection, row open → detail panel, and Pagination.
 * Horizontal inset is 1.5rem (`px-6`) — same as Provider/Instance Detail.
 */
const RequestsInboxPage = () => {
  const { t } = useTranslation('admin-studio');
  const navigate = useNavigate();
  const toast = useToast();
  const { openRequestIntegration } = useIntegrations();
  const { canManageIntegrations } = useIntegrationsPermissions();
  const { requesterId } = useEffectiveRequester();

  // Persona (CT-4203). `isUserView` swaps the tenant-wide admin inbox for the
  // end-user "My requests" read-only view (own submissions only). Driven by the
  // domain-wide persona seam: in V1 RBAC is deferred (CT-3900) so the PersonaDemoBar
  // flips canManageIntegrations; when RBAC lands the real role drives this unchanged.
  const isUserView = !canManageIntegrations;

  const { entries, counts, status, setStatus, search, setSearch, isLoading, error, reload } =
    useRequestsInbox({ requesterId: isUserView ? requesterId : undefined });
  const [panelTicket, setPanelTicket] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);

  const totalPages = Math.max(1, Math.ceil(entries.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * perPage;
  const pageRows = useMemo(
    () => entries.slice(pageStart, pageStart + perPage),
    [entries, pageStart, perPage],
  );
  const rangeLabel =
    entries.length === 0 ? '0' : `${pageStart + 1}–${Math.min(pageStart + perPage, entries.length)}`;

  const resetPage = useCallback((updater) => {
    updater();
    setPage(1);
  }, []);

  const searchProps = useMemo(
    () => ({
      value: search,
      onChange: (e) => resetPage(() => setSearch(e?.target ? e.target.value : e)),
      onClear: () => resetPage(() => setSearch('')),
      placeholder: t('integrations.requests.searchPlaceholder'),
    }),
    [search, setSearch, resetPage, t],
  );

  const filtersConfig = useMemo(
    () => [
      {
        id: 'status',
        label: t('integrations.requests.inbox.statusFilter'),
        multiple: false,
        value: status === 'all' ? [] : [status],
        onChange: (next) => resetPage(() => setStatus(next[0] ?? 'all')),
        options: REQUEST_STATUS_FILTERS.map((value) => ({
          value,
          label: t(`integrations.requests.tabs.${value}`),
        })),
      },
    ],
    [status, setStatus, resetPage, t],
  );

  const getRowId = useCallback((row) => row.ticket_number, []);
  const pageIds = useMemo(() => pageRows.map(getRowId), [pageRows, getRowId]);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  const handleApprove = useCallback(
    async (row) => {
      try {
        await approveRequest(row.ticket_number);
        toast.success({ message: t('integrations.requests.inbox.actions.approved', { name: row.name }), canClose: true });
        reload();
      } catch (err) {
        toast.error({ message: err?.message ?? t('common.requestFailed'), canClose: true });
      }
    },
    [reload, t, toast],
  );

  const handleReject = useCallback(
    async (row) => {
      try {
        await rejectRequest(row.ticket_number, t('integrations.requests.inbox.actions.rejectReason'));
        toast.success({ message: t('integrations.requests.inbox.actions.rejected', { name: row.name }), canClose: true });
        reload();
      } catch (err) {
        toast.error({ message: err?.message ?? t('common.requestFailed'), canClose: true });
      }
    },
    [reload, t, toast],
  );

  const handleAssign = useCallback(() => {
    toast.info({ message: t('integrations.requests.inbox.actions.assignSoon'), canClose: true });
  }, [t, toast]);

  const selectedCount = selectedIds.size;
  const selectedTickets = useMemo(() => [...selectedIds], [selectedIds]);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const handleBulkApprove = useCallback(async () => {
    if (selectedTickets.length === 0) return;
    try {
      await bulkUpdateRequests(selectedTickets, 'approved');
      toast.success({
        message: t('integrations.requests.inbox.bulk.approved', { count: selectedTickets.length }),
        canClose: true,
      });
      clearSelection();
      reload();
    } catch (err) {
      toast.error({ message: err?.message ?? t('common.requestFailed'), canClose: true });
    }
  }, [clearSelection, reload, selectedTickets, t, toast]);

  const handleBulkReject = useCallback(async () => {
    if (selectedTickets.length === 0) return;
    try {
      await bulkUpdateRequests(selectedTickets, 'rejected');
      toast.success({
        message: t('integrations.requests.inbox.bulk.rejected', { count: selectedTickets.length }),
        canClose: true,
      });
      clearSelection();
      reload();
    } catch (err) {
      toast.error({ message: err?.message ?? t('common.requestFailed'), canClose: true });
    }
  }, [clearSelection, reload, selectedTickets, t, toast]);

  const selection = useMemo(
    () => ({
      enabled: !isUserView,
      allSelected,
      onToggleAll: () => {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (allSelected) pageIds.forEach((id) => next.delete(id));
          else pageIds.forEach((id) => next.add(id));
          return next;
        });
      },
      getRowId,
      isRowSelected: (row) => selectedIds.has(getRowId(row)),
      onToggleRow: (row) => {
        const id = getRowId(row);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        });
      },
      selectAllAriaLabel: t('integrations.requests.inbox.selectAll'),
      rowSelectAriaLabel: (row) => t('integrations.requests.inbox.selectRow', { name: row.name }),
    }),
    [allSelected, getRowId, isUserView, pageIds, selectedIds, t],
  );

  const columns = useMemo(() => {
    const cols = [
      {
        key: 'status',
        label: t('integrations.requests.columns.status'),
        headerClassName: 'w-[148px] !pl-2',
        cellClassName: '!pl-2',
        render: (value, row) => <RequestStatusChip status={value} priority={row.priority} />,
      },
      {
        key: 'name',
        label: t('integrations.requests.columns.request'),
        truncate: true,
        render: (value, row) => (
          <div className="min-w-0 w-full overflow-hidden py-0.5">
            <p className="truncate text-[13px] font-semibold leading-snug text-content">{value}</p>
            {row.business_intent ? (
              <p className="mt-0.5 truncate text-[12px] leading-snug text-content-secondary" title={row.business_intent}>
                {row.business_intent}
              </p>
            ) : null}
            <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
              {actionLabel(row) ? (
                <span className="text-[11px] text-content-faint">{actionLabel(row)}</span>
              ) : null}
              {SENSITIVITY_TAGS.filter((key) => row.data_sensitivity?.[key] === 'yes').map((key) => (
                <Badge
                  key={key}
                  variant="neutral"
                  size="small"
                  className="!h-auto !rounded-md !border !border-[rgba(234,179,8,0.45)] !bg-transparent !px-1.5 !py-0.5 !text-[10px] !font-semibold !uppercase !tracking-[0.04em] !text-[#EAB308]"
                >
                  {t(`integrations.requests.inbox.sensitivity.${key}`)}
                </Badge>
              ))}
            </div>
          </div>
        ),
      },
      {
        key: 'requester',
        label: t('integrations.requests.columns.requester'),
        headerClassName: 'w-[220px]',
        render: (value, row) => (
          <span className="flex items-center gap-2.5">
            <Avatar alt={value} initials={initialsOf(value)} className={REQUESTER_AVATAR_CLASS} />
            <span className="min-w-0">
              <span className="block truncate text-[13px] text-content">{value}</span>
              <span className="block text-[12px] text-content-faint">
                {formatRelativeTime(row.date_created, t)}
              </span>
            </span>
          </span>
        ),
      },
      {
        key: 'quote_usd',
        label: t('integrations.requests.columns.quote'),
        headerClassName: 'w-[140px]',
        render: (value, row) => {
          if (row.status === 'pending') {
            return (
              <span className="text-[13px] text-content-secondary">
                {t('integrations.requests.inbox.awaitingQuote')}
              </span>
            );
          }
          if (row.status === 'delivered' || row.status === 'rejected' || row.status === 'approved') {
            return <span className="text-[13px] text-content-secondary">{t('integrations.requests.inbox.closed')}</span>;
          }
          if (value == null) return <span className="text-content-faint">—</span>;
          return (
            <span>
              <span className="block text-[13px] font-semibold text-content">{formatUsd(value)}</span>
              {row.eta_weeks != null ? (
                <span className="block text-[12px] text-content-faint">
                  {t('integrations.requests.inbox.eta', { weeks: row.eta_weeks })}
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        key: 'ticket_number',
        label: '',
        headerClassName: 'w-12',
        cellClassName: '!pr-2',
        render: (_, row) => (
          <RequestRowActionsMenu
            name={row.name}
            onOpen={() => setPanelTicket(row.ticket_number)}
            onApprove={() => handleApprove(row)}
            onReject={() => handleReject(row)}
            onAssign={handleAssign}
          />
        ),
      },
    ];
    // End-user "My requests" is read-only: drop the row-actions column.
    return isUserView ? cols.filter((column) => column.key !== 'ticket_number') : cols;
  }, [handleApprove, handleAssign, handleReject, isUserView, t]);

  const waiting = counts.waiting_on_you ?? 0;

  const pager = (
    <Pagination
      className="shrink-0"
      rangeLabel={rangeLabel}
      totalItems={entries.length}
      currentPage={safePage}
      totalPages={totalPages}
      perPage={perPage}
      hasPrevious={safePage > 1}
      hasNext={safePage < totalPages}
      onPrevPage={() => setPage(Math.max(1, safePage - 1))}
      onNextPage={() => setPage(Math.min(totalPages, safePage + 1))}
      onPageSizeChange={(size) => {
        setPerPage(size);
        setPage(1);
      }}
    />
  );

  return (
    <main className={PAGE_WRAP}>
      <div className={`${PAGE_INNER} flex-1 overflow-y-auto pb-4 pt-3.5 scrollbar-aimsos-auto-sm`}>
        <BreadcrumbNav
          size="sm"
          items={[
            { label: t('integrations.home.title'), path: '../integrations' },
            {
              label: isUserView
                ? t('integrations.requests.mine.title')
                : t('integrations.requests.title'),
              path: '.',
            },
          ]}
          onNavigate={(path) => navigate(path)}
        />

        <div className="mb-4 mt-4 flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-2xl font-bold tracking-[-0.015em] text-content">
              {isUserView ? t('integrations.requests.mine.title') : t('integrations.requests.title')}
            </h1>
            {isUserView ? (
              <p className="text-[13px] text-content-secondary">
                {t('integrations.requests.mine.subtitle')}
              </p>
            ) : (
              <p className="text-[13px] text-content-secondary">
                {t('integrations.requests.inbox.total', { count: counts.total ?? entries.length })}
                {' · '}
                <span className="font-medium text-[#EAB308]">
                  {t('integrations.requests.subtitle', { count: waiting })}
                </span>
              </p>
            )}
          </div>
          <Button variant="classicPrimary" size="md" leftIcon={Plus} onClick={() => openRequestIntegration()}>
            {t('integrations.requests.inbox.newRequest')}
          </Button>
        </div>

        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : error ? (
          <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />
        ) : isUserView && entries.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-12">
            <StatusMessage icon={Store} title={t('integrations.requests.mine.emptyTitle')} />
            <Button
              variant="primary"
              size="sm"
              leftIcon={Store}
              onClick={() => navigate('../integrations')}
            >
              {t('integrations.requests.mine.browseMarketplace')}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Filters
              searchProps={searchProps}
              filters={filtersConfig}
              showAllFilters={false}
              maxFilters={1}
              onClearAll={() => {
                setSearch('');
                setStatus('all');
                setPage(1);
              }}
            />

            {!isUserView && selectedCount > 0 ? (
              <RequestsBulkBar
                count={selectedCount}
                onApprove={handleBulkApprove}
                onReject={handleBulkReject}
                onReassign={handleAssign}
                onClear={clearSelection}
              />
            ) : null}

            <Card className="overflow-hidden">
              <DataTable
                variant="workspace"
                columns={columns}
                data={pageRows}
                minTableWidth={880}
                selection={selection}
                getRowId={getRowId}
                emptyMessage={
                  status === 'all' && !search.trim() && entries.length === 0
                    ? t('integrations.requests.inbox.emptyNone')
                    : t('integrations.requests.inbox.empty')
                }
                onRowClick={(row) => setPanelTicket(row.ticket_number)}
                rowClassName={(row) =>
                  row.status === 'pending' ? 'shadow-[inset_3px_0_0_#EAB308]' : ''
                }
                classNames={{
                  row: 'hover:!bg-[#E8F1FF] dark:hover:!bg-[#152A45]',
                  td: '!h-auto py-3 align-top',
                  th: '!h-12',
                }}
              />
            </Card>
          </div>
        )}

        <RequestDetailPanel
          ticket={panelTicket}
          onClose={() => setPanelTicket(null)}
          onChanged={reload}
          readOnly={isUserView}
        />
      </div>

      {!isLoading && !error ? <div className={`${PAGE_INNER} shrink-0 py-3`}>{pager}</div> : null}
    </main>
  );
};

export default RequestsInboxPage;
